import {
  Autocomplete,
  Box,
  SxProps,
  Theme,
  Typography,
  createFilterOptions,
  useTheme,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { DownArrowIcon } from "components/icons";
import React, { useMemo } from "react";
import { InputRoot } from "./InputWithLabelRoot";
import { convertStringToSentenceCase } from "utilz/helpers";

const filter = createFilterOptions<any>();

interface CreatableSearchableSelectProps {
  control?: any;
  label?: string;
  name: string;
  options: any[]; // Array of strings or objects { label: string, value: any }
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  fullWidth?: boolean;
  required?: boolean;
  sx?: SxProps<Theme>;
}

const CreatableSearchableSelect = ({
  control,
  label,
  name,
  options,
  placeholder,
  error,
  errorMessage,
  fullWidth = true,
  required = false,
  sx,
}: CreatableSearchableSelectProps) => {
  const theme = useTheme();

  const normalizedOptions = useMemo(() => {
    return options.map((opt) =>
      typeof opt === "string" ? { label: opt, value: opt } : opt,
    );
  }, [options]);

  const customStyles = {
    "& .MuiInputBase-root": {
      height: "32px",
      ...theme.typography["body-medium"],
      overflow: "clip",
      backgroundColor: "white",
      //   padding: "0 4px !important", // Match Input.tsx padding
    },
    // Center the content and placeholder
    "& .MuiAutocomplete-input": {
      padding: "0 12px !important",
      height: "100%",
      display: "flex",
      alignItems: "center",
    },
    ...sx,
  };

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        position: "relative",
      }}
    >
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value, ref } }) => (
          <Autocomplete
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            autoHighlight={true}
            handleHomeEndKeys
            onBlur={(event: any) => {
              const val = event.target.value;
              if (val) {
                onChange(val);
              }
            }}
            options={normalizedOptions}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option.inputValue) return option.inputValue;
              return option.label;
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const isExisting = options.some(
                (option) => inputValue === option.label,
              );
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  label: `Add "${inputValue}"`,
                });
              }
              return filtered;
            }}
            value={
              typeof value === "string"
                ? normalizedOptions.find((opt) => opt.value === value) || value
                : value
            }
            onChange={(event, newValue) => {
              if (typeof newValue === "string") {
                onChange(newValue);
              } else if (newValue && newValue.inputValue) {
                onChange(newValue.inputValue);
              } else {
                onChange(newValue?.value || "");
              }
            }}
            renderInput={(params) => (
              <InputRoot
                {...params}
                name={name}
                size="small"
                placeholder={placeholder}
                error={error}
                sx={customStyles}
                inputRef={ref}
                InputLabelProps={{
                  sx: {
                    fontFamily: theme.typography["body-medium"].fontFamily,
                    fontWeight: theme.typography["body-medium"].fontWeight,
                    fontSize: theme.typography["body-medium"].fontSize,
                    transform: "translate(14px,6px) scale(1)",
                    "&.MuiFormLabel-filled": {
                      transform: "translate(14px, -9px) scale(.75)",
                    },
                    "&.Mui-focused": {
                      color: theme.palette.purple.main,
                      transform: "translate(14px, -9px) scale(.75)",
                    },
                  },
                }}
                label={
                  label && (
                    <Typography
                      variant="body-medium"
                      color={error ? "error" : "inherit"}
                      textTransform={"inherit"}
                    >
                      {typeof label === "string"
                        ? convertStringToSentenceCase(label)
                        : label}
                      {required && " *"}
                    </Typography>
                  )
                }
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    ...theme.typography["body-medium"],
                    "& .MuiOutlinedInput-notchedOutline": {
                      // Ensure Autocomplete's notched outline doesn't conflict
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value || option.inputValue}>
                <Typography variant="body-small">{option.label}</Typography>
              </li>
            )}
            popupIcon={<DownArrowIcon />}
            fullWidth={fullWidth}
          />
        )}
      />
      <Box sx={{ paddingLeft: "12px", marginTop: "2px" }}>
        {error && (
          <Typography variant="caption-3" color="error">
            {errorMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export { CreatableSearchableSelect };
